import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Progress } from "../ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  User,
  Mail,
  Edit,
  Save,
  FileText,
  Video,
  Lock,
  ExternalLink,
  Camera,
  Award,
  Plus,
  X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import {
  profileAPI,
  dashboardAPI,
  resumeAPI,
  coverLetterAPI,
  videoInterviewAPI,
  resolveAssetUrl,
} from "../../services/api";
import PhotoUploadModal from "./PhotoUploadModal";
import AvatarLightbox from "./AvatarLightbox";

const EMPLOYMENT_OPTIONS = ["Full-time", "Part-time", "Contract", "Internship", "Open to any"];
const AVAILABILITY_OPTIONS = ["Immediate", "2 weeks notice", "1 month notice", "Flexible"];
const RELOCATE_OPTIONS = ["Yes", "No", "Open to discussion", "Remote only"];

const ProfileSection = ({ user, onProfileUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    location: "",
    linkedin: "",
    website: "",
    bio: "",
    professional_title: "",
    employment_preference: "",
    availability: "",
    willingness_to_relocate: "",
    languages_spoken: [],
    awards: [],
  });
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarOriginal, setAvatarOriginal] = useState("");
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [languageInput, setLanguageInput] = useState("");

  const [dashboardStats, setDashboardStats] = useState(null);
  const [hasResume, setHasResume] = useState(false);
  const [hasCoverLetter, setHasCoverLetter] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);

  useEffect(() => {
    loadProfile();
    loadEverything();
  }, []);

  const loadEverything = async () => {
    try {
      const [stats, resume, cl, vid] = await Promise.all([
        dashboardAPI.getStats().catch(() => null),
        resumeAPI.getResume().catch(() => null),
        coverLetterAPI.getCoverLetter().catch(() => null),
        videoInterviewAPI.getVideoInterview().catch(() => null),
      ]);
      setDashboardStats(stats);
      setHasResume(Boolean(resume));
      setHasCoverLetter(Boolean(cl));
      setHasVideo(Boolean(vid && (vid.video_url || vid.status === "completed")));
    } catch (e) {
      console.warn("Profile dashboard signal load failed:", e);
    }
  };

  const loadProfile = async () => {
    try {
      setLoading(true);
      const profileData = await profileAPI.getProfile();
      setProfile(profileData);
      setFormData({
        full_name:
          profileData.full_name ||
          (user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : ""),
        email: profileData.email || user?.email || "",
        phone: profileData.phone || "",
        location: profileData.location || "",
        linkedin: profileData.linkedin || "",
        website: profileData.website || "",
        bio: profileData.bio || "",
        professional_title: profileData.professional_title || "",
        employment_preference: profileData.employment_preference || "",
        availability: profileData.availability || "",
        willingness_to_relocate: profileData.willingness_to_relocate || "",
        languages_spoken: Array.isArray(profileData.languages_spoken)
          ? profileData.languages_spoken
          : [],
        awards: Array.isArray(profileData.awards) ? profileData.awards : [],
      });
      setAvatarPreview(profileData.avatar_url || "");
      setAvatarOriginal(profileData.avatar_original_url || "");
    } catch (error) {
      console.error("Failed to load profile:", error);
      toast.error("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      // Strip out empty selects/strings sent by the form so we don't
      // overwrite a real value with an accidental "".
      const payload = { ...formData };
      payload.languages_spoken = (payload.languages_spoken || [])
        .map((l) => (l || "").trim())
        .filter(Boolean);
      payload.awards = (payload.awards || [])
        .map((a) => ({
          title: (a.title || "").trim(),
          year: (a.year || "").trim() || null,
          issuer: (a.issuer || "").trim() || null,
        }))
        .filter((a) => a.title);
      const updatedProfile = await profileAPI.updateProfile(payload);
      setProfile(updatedProfile);
      setIsEditing(false);
      toast.success("Profile updated. Changes will cascade to your resume, cover letter, and public page.");
      if (onProfileUpdate) onProfileUpdate();
      loadEverything();
    } catch (error) {
      console.error("Failed to update profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelectChange = (field) => (value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addLanguage = () => {
    const v = languageInput.trim();
    if (!v) return;
    if (formData.languages_spoken.includes(v)) {
      setLanguageInput("");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      languages_spoken: [...prev.languages_spoken, v],
    }));
    setLanguageInput("");
  };

  const removeLanguage = (lang) => {
    setFormData((prev) => ({
      ...prev,
      languages_spoken: prev.languages_spoken.filter((l) => l !== lang),
    }));
  };

  const addAward = () => {
    setFormData((prev) => ({
      ...prev,
      awards: [...(prev.awards || []), { title: "", year: "", issuer: "" }],
    }));
  };

  const updateAward = (idx, field, value) => {
    setFormData((prev) => {
      const next = [...(prev.awards || [])];
      next[idx] = { ...next[idx], [field]: value };
      return { ...prev, awards: next };
    });
  };

  const removeAward = (idx) => {
    setFormData((prev) => ({
      ...prev,
      awards: (prev.awards || []).filter((_, i) => i !== idx),
    }));
  };

  // Resolve a relative (/api/media/...) avatar URL to a fully-qualified
  // browser-loadable URL. data: URLs and absolute URLs pass through.
  const avatarSrc = resolveAssetUrl(avatarPreview);

  const publicPageUrl = user?.id
    ? `${window.location.origin}/video/${user.id}`
    : null;

  const completionTasks = [
    { task: "Full Name", completed: Boolean((formData.full_name || "").trim()) },
    { task: "Email", completed: Boolean((formData.email || "").trim()) },
    { task: "Phone", completed: Boolean((formData.phone || "").trim()) },
    { task: "Location", completed: Boolean((formData.location || "").trim()) },
    { task: "Professional Summary", completed: Boolean((formData.bio || "").trim()) },
    { task: "Profile Photo", completed: Boolean(avatarPreview) },
    { task: "Resume", completed: hasResume },
    { task: "Cover Letter", completed: hasCoverLetter },
    { task: "Video Interview", completed: hasVideo },
  ];

  const completedTasks = completionTasks.filter((t) => t.completed).length;
  const completionPercentage = Math.round((completedTasks / completionTasks.length) * 100);

  const resumeBadge = hasResume
    ? { label: "Complete", className: "bg-green-100 text-green-800" }
    : { label: "Not Started", variant: "outline" };
  const coverLetterBadge = hasCoverLetter
    ? { label: "Complete", className: "bg-green-100 text-green-800" }
    : { label: "Not Started", variant: "outline" };
  const videoStatusRaw =
    dashboardStats?.video_status || (hasVideo ? "Completed" : "Not Started");
  const videoBadge =
    videoStatusRaw === "Completed"
      ? { label: "Complete", className: "bg-green-100 text-green-800" }
      : videoStatusRaw === "Stitching"
      ? { label: "Stitching…", className: "bg-amber-100 text-amber-800" }
      : videoStatusRaw === "Ready to Finalize"
      ? { label: "Ready to Finalize", className: "bg-blue-100 text-blue-800" }
      : { label: videoStatusRaw, variant: "outline" };

  if (loading && !profile) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
          <p className="text-gray-600 mt-1">Manage your personal information and preferences</p>
        </div>
        <div className="flex items-center gap-2">
          {publicPageUrl && (
            <Button
              variant="outline"
              onClick={() => window.open(publicPageUrl, "_blank", "noopener")}
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
              data-testid="view-public-candidate-page-btn"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View your public candidate page
            </Button>
          )}
          <Button
            onClick={() => (isEditing ? handleSave() : setIsEditing(true))}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="profile-edit-save-btn"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : isEditing ? (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            ) : (
              <>
                <Edit className="h-4 w-4 mr-2" />
                Edit Profile
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Profile Information */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <User className="h-5 w-5 mr-2 text-blue-600" />
                Personal Information
              </CardTitle>
              <CardDescription>
                Your basic information that appears on your resume and applications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Iter-30: signature widget removed from Profile — it
                  now lives at the final step of the Cover Letter
                  Generator where it's most relevant. Profile remains
                  the canonical storage source behind the scenes. */}
              <div className="flex items-start gap-4 mb-6">
                <button
                  type="button"
                  onClick={() => avatarPreview && setLightboxOpen(true)}
                  className={`relative group block focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-2xl ${
                    avatarPreview ? "cursor-zoom-in" : "cursor-default"
                  }`}
                  aria-label={avatarPreview ? "View larger profile photo" : "Profile photo"}
                  data-testid="profile-avatar-button"
                >
                  <div
                    className="h-28 w-28 sm:h-32 sm:w-32 rounded-2xl overflow-hidden ring-1 ring-slate-200 bg-gradient-to-br from-blue-100 to-indigo-100 shadow-sm"
                    data-testid="profile-avatar"
                  >
                    {avatarSrc ? (
                      <img
                        src={avatarSrc}
                        alt={`${formData.full_name || "Profile"} photo`}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-blue-700 text-2xl font-bold">
                        {(formData.full_name || "U")
                          .split(/\s+/)
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((n) => n[0]?.toUpperCase() || "")
                          .join("") || "U"}
                      </div>
                    )}
                  </div>
                  {avatarPreview && (
                    <span className="absolute inset-0 rounded-2xl bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <span className="text-white text-xs font-semibold uppercase tracking-wide">View</span>
                    </span>
                  )}
                </button>
                <div className="pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPhotoModalOpen(true)}
                    data-testid="profile-photo-trigger"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    {avatarPreview ? "Change photo" : "Add photo"}
                  </Button>
                  <p className="text-xs text-gray-500 mt-1 max-w-[240px]">
                    Upload or take with camera. Pan + zoom to frame.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Your full name"
                    className="border-blue-200 focus:border-blue-500"
                    data-testid="profile-input-full-name"
                  />
                  <p className="text-xs text-gray-500">
                    This is the canonical name used on your resume, cover letter, and public page.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-1">
                    Contact Email
                    <Lock className="h-3 w-3 text-slate-400" />
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="you@example.com"
                    className="border-blue-200 focus:border-blue-500"
                    data-testid="profile-input-email"
                  />
                  <p className="text-xs text-gray-500">
                    Recruiters will use this email. Login email
                    <strong className="ml-1">{user?.email}</strong> is unchanged.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="professional_title">Professional Title</Label>
                  <Input
                    id="professional_title"
                    name="professional_title"
                    value={formData.professional_title}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="e.g., Senior Software Engineer"
                    className="border-blue-200 focus:border-blue-500"
                    data-testid="profile-input-professional-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="border-blue-200 focus:border-blue-500"
                    data-testid="profile-input-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="border-blue-200 focus:border-blue-500"
                    data-testid="profile-input-location"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Professional Summary</Label>
                <Textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  rows={3}
                  className="border-blue-200 focus:border-blue-500"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="linkedin">LinkedIn Profile</Label>
                  <Input
                    id="linkedin"
                    name="linkedin"
                    value={formData.linkedin}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="border-blue-200 focus:border-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Personal Website</Label>
                  <Input
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="border-blue-200 focus:border-blue-500"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Work preferences */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Award className="h-5 w-5 mr-2 text-blue-600" />
                Preferences & Achievements
              </CardTitle>
              <CardDescription>
                What you're looking for and what you've accomplished. Shown on your public candidate page.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Employment preference</Label>
                  <Select
                    value={formData.employment_preference || undefined}
                    onValueChange={handleSelectChange("employment_preference")}
                    disabled={!isEditing}
                  >
                    <SelectTrigger data-testid="profile-select-employment-preference">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {EMPLOYMENT_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Availability</Label>
                  <Select
                    value={formData.availability || undefined}
                    onValueChange={handleSelectChange("availability")}
                    disabled={!isEditing}
                  >
                    <SelectTrigger data-testid="profile-select-availability">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {AVAILABILITY_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Willing to relocate</Label>
                  <Select
                    value={formData.willingness_to_relocate || undefined}
                    onValueChange={handleSelectChange("willingness_to_relocate")}
                    disabled={!isEditing}
                  >
                    <SelectTrigger data-testid="profile-select-relocate">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {RELOCATE_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Languages spoken</Label>
                <div className="flex flex-wrap gap-2 mb-2" data-testid="profile-languages-list">
                  {(formData.languages_spoken || []).map((lang) => (
                    <Badge
                      key={lang}
                      variant="secondary"
                      className="pl-2 pr-1 py-1 flex items-center gap-1"
                    >
                      {lang}
                      {isEditing && (
                        <button
                          type="button"
                          onClick={() => removeLanguage(lang)}
                          className="ml-1 rounded-full hover:bg-slate-200 p-0.5"
                          aria-label={`Remove ${lang}`}
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  ))}
                  {(formData.languages_spoken || []).length === 0 && (
                    <span className="text-xs text-slate-400">None added yet.</span>
                  )}
                </div>
                {isEditing && (
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g., English"
                      value={languageInput}
                      onChange={(e) => setLanguageInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addLanguage();
                        }
                      }}
                      data-testid="profile-language-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addLanguage}
                      data-testid="profile-language-add-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Awards & achievements</Label>
                <div className="space-y-2" data-testid="profile-awards-list">
                  {(formData.awards || []).map((award, idx) => (
                    <div key={idx} className="grid md:grid-cols-12 gap-2 items-center">
                      <Input
                        className="md:col-span-6"
                        placeholder="Award title"
                        value={award.title || ""}
                        onChange={(e) => updateAward(idx, "title", e.target.value)}
                        disabled={!isEditing}
                        data-testid={`profile-award-title-${idx}`}
                      />
                      <Input
                        className="md:col-span-3"
                        placeholder="Issuer"
                        value={award.issuer || ""}
                        onChange={(e) => updateAward(idx, "issuer", e.target.value)}
                        disabled={!isEditing}
                      />
                      <Input
                        className="md:col-span-2"
                        placeholder="Year"
                        value={award.year || ""}
                        onChange={(e) => updateAward(idx, "year", e.target.value)}
                        disabled={!isEditing}
                      />
                      {isEditing && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="md:col-span-1 text-red-600 hover:bg-red-50"
                          onClick={() => removeAward(idx)}
                          aria-label="Remove award"
                        >
                          <XIcon className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {(formData.awards || []).length === 0 && (
                    <p className="text-xs text-slate-400">No awards yet.</p>
                  )}
                </div>
                {isEditing && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addAward}
                    data-testid="profile-award-add-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add award
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Profile Completion & Quick Stats */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Profile Completion</CardTitle>
              <CardDescription>
                Complete your profile to improve your job search success
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-blue-600" data-testid="profile-completion-pct">
                    {dashboardStats?.profile_completion ?? completionPercentage}%
                  </span>
                  <Badge
                    variant={
                      (dashboardStats?.profile_completion ?? completionPercentage) === 100
                        ? "default"
                        : "secondary"
                    }
                  >
                    {(dashboardStats?.profile_completion ?? completionPercentage) === 100
                      ? "Complete"
                      : "In Progress"}
                  </Badge>
                </div>
                <Progress
                  value={dashboardStats?.profile_completion ?? completionPercentage}
                  className="h-3"
                />

                <div className="space-y-2 mt-4">
                  {completionTasks.map((task, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <span className={task.completed ? "text-green-600" : "text-gray-500"}>
                        {task.task}
                      </span>
                      <span className={task.completed ? "text-green-600" : "text-gray-400"}>
                        {task.completed ? "✓" : "○"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Stats</CardTitle>
              <CardDescription>
                A snapshot of what's complete on your candidate package.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between" data-testid="profile-quickstat-resume">
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 text-blue-600 mr-2" />
                    <span className="text-sm">Resume</span>
                  </div>
                  {resumeBadge.className ? (
                    <Badge className={resumeBadge.className}>{resumeBadge.label}</Badge>
                  ) : (
                    <Badge variant={resumeBadge.variant}>{resumeBadge.label}</Badge>
                  )}
                </div>
                <div className="flex items-center justify-between" data-testid="profile-quickstat-cover-letter">
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 text-blue-600 mr-2" />
                    <span className="text-sm">Cover Letter</span>
                  </div>
                  {coverLetterBadge.className ? (
                    <Badge className={coverLetterBadge.className}>{coverLetterBadge.label}</Badge>
                  ) : (
                    <Badge variant={coverLetterBadge.variant}>{coverLetterBadge.label}</Badge>
                  )}
                </div>
                <div className="flex items-center justify-between" data-testid="profile-quickstat-video">
                  <div className="flex items-center">
                    <Video className="h-4 w-4 text-blue-600 mr-2" />
                    <span className="text-sm">Video Profile</span>
                  </div>
                  {videoBadge.className ? (
                    <Badge className={videoBadge.className}>{videoBadge.label}</Badge>
                  ) : (
                    <Badge variant={videoBadge.variant}>{videoBadge.label}</Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <PhotoUploadModal
        open={photoModalOpen}
        onOpenChange={setPhotoModalOpen}
        hasPhoto={Boolean(avatarPreview)}
        originalUrl={avatarOriginal || avatarPreview}
        onChange={(payload) => {
          // Iter-28: payload is { avatar_url, avatar_original_url }
          // for upload/recrop. Delete sends empty strings.
          if (payload && typeof payload === "object") {
            setAvatarPreview(payload.avatar_url || "");
            setAvatarOriginal(payload.avatar_original_url || "");
          } else {
            // Defensive: older onChange shape passed the URL string
            // directly. Keep it working.
            setAvatarPreview(payload || "");
          }
          loadEverything();
          if (onProfileUpdate) onProfileUpdate();
        }}
      />

      <AvatarLightbox
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        src={avatarSrc}
        alt={`${formData.full_name || "Profile"} photo`}
      />
    </div>
  );
};

export default ProfileSection;
